import {
  InjectableType,
  ɵComponentType as ComponentType,
  ɵDirectiveType as DirectiveType,
} from '@angular/core';

import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  isObservable,
  merge,
  Observable,
  ObservableInput,
  ObservedValueOf,
  of,
  OperatorFunction,
  scheduled,
} from 'rxjs';
import { debounceTime, filter, first, map, startWith, switchMap, switchMapTo, tap } from 'rxjs/operators';

export class ReadonlyBehaviorSubject<T> {
  get value(): T {
    return this.subject$.value;
  }

  get value$(): Observable<T> {
    return this.subject$;
  }

  constructor(private readonly subject$: BehaviorSubject<T>) {
  }
}

export const inputIsNotNullOrUndefined = <T>(input: T): input is Exclude<T, null | undefined> => input !== null && input !== undefined;
export const inputIsNotNull = <T>(input: T): input is Exclude<T, null> => input !== null;
export const isNotNull = <T>(): (source$: Observable<T>) => Observable<Exclude<T, null>> =>
  filter(inputIsNotNull);
export const isNotNullOrUndefined = <T>(): (source$: Observable<T>) => Observable<Exclude<T, null | undefined>> =>
  filter(inputIsNotNullOrUndefined);

export const firstNotNullOrUndefined = <T>(): (source$: Observable<T>) => Observable<Exclude<T, null | undefined>> =>
  first(inputIsNotNullOrUndefined);

export const maybeSwitchMap =
  <T, O extends ObservableInput<any>>(project: (value: T) => O): OperatorFunction<T | null | undefined, ObservedValueOf<O> | undefined> =>
  switchMap((maybeValue: T | null | undefined) => maybeValue ? project(maybeValue) : of(undefined));

/**
 * Determine if the argument is shaped like a Promise
 * allow any Promise/A+ compliant thenable.
 * It's up to the caller to ensure that obj.then conforms to the spec
 */
export const isPromise = <T = any>(obj: any): obj is Promise<T> => !!obj && typeof obj.then === 'function';

export const wrapIntoObservable = <T>(value: T | Promise<T> | Observable<T>): Observable<T> => {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    // Use `Promise.resolve()` to wrap promise-like instances.
    // Required ie when a Resolver returns a AngularJS `$q` promise to correctly trigger the
    // change detection.
    return from(Promise.resolve(value));
  }

  return of(value);
};

export const waitAnimationFrame = () => switchMapTo(scheduled([1], animationFrameScheduler));

function decorateIonViewDidEnter(
  ionViewDidEnter: (() => void) | null | undefined,
) {
  return function (this: any) {
    // Invoke the original `ionViewDidEnter` if it exists
    ionViewDidEnter && ionViewDidEnter.call(this);
    if (!this.filterIfIonViewDidEnterActive$.value) {
      this.filterIfIonViewDidEnterActive$.next(true);
    }
  }
}

function decorateIonViewWillLeave(
  ionViewWillLeave: (() => void) | null | undefined,
) {
  return function (this: any) {
    // Invoke the original `ionViewWillLeave` if it exists
    ionViewWillLeave && ionViewWillLeave.call(this);
    if (this.filterIfIonViewDidEnterActive$.value) {
      this.filterIfIonViewDidEnterActive$.next(false);
    }
  }
}

function decorateFilterOnComponent<T>(
  type: InjectableType<T> | DirectiveType<T> | ComponentType<T>,
): void {
  type.prototype.filterIfIonViewDidEnterActive$ = new BehaviorSubject(true);
  type.prototype.ionViewDidEnter = decorateIonViewDidEnter(type.prototype.ionViewDidEnter);
  type.prototype.ionViewWillLeave = decorateIonViewWillLeave(type.prototype.ionViewWillLeave);
}

export function ionViewActive(): ClassDecorator {
  return (type: any) => decorateFilterOnComponent(type);
}

export function filterIfIonViewActive(type: any) {
  return switchMap(() => type.filterIfIonViewDidEnterActive$.pipe(
    filter(active => !!active),
  ));
}

export function intervalIfIonViewActive(type: any, opt: { interval: number , forceTrigger$: Observable<void> }): Observable<any> {
  let trigger = false;
  return combineLatest([
    merge(
      opt.forceTrigger$,
      interval(opt.interval),
    ).pipe(tap(() => trigger = true)),
    type.filterIfIonViewDidEnterActive$,
  ]).pipe(
    map(([_, isActive]) => {
      if (isActive && trigger) {
        trigger = false;
        return true;
      }
      return false;
    }),
    filter(active => !!active),
    startWith(false),
  );
}
